<template>
  <div>
    <vue-good-table
      mode="remote"
      theme="polar-bear"
      :rows="tableData.rows"
      :columns="tableData.columns"
      :total-rows="tableData.totalRows"
      :is-loading.sync="tableData.isLoading"
      :pagination-options="{
        enabled: false,
      }"
      :search-options="{
        enabled: false,
      }"
      :sort-options="{
        enabled: false,
      }"
      styleClass="vgt-table striped bordered"
      @on-page-change="onPageChange"
      @on-per-page-change="onPerPageChange"
    >
      <template slot="table-row" slot-scope="props">
        <template v-if="props.column.field == 'name'">
          {{ props.row.name }}
          <br />
          <span class="block mt-2"
            ><strong class="block">UUID:</strong>{{ props.row.uuid }}</span
          >
        </template>
        <template v-else-if="props.column.field == 'bank_accounts'">
          <template v-if="props.row.bank_accounts.length > 0">
            <span
              class="flex justify-between items-start mb-2"
              v-for="bank in props.row.bank_accounts"
              :key="bank.id"
            >
              <span class="block">
                <router-link
                  v-if="userCan('update.member-bank-account')"
                  :to="{
                    name: 'MemberBankEdit',
                    params: { memberId: props.row.uuid, id: bank.uuid },
                  }"
                  class="link"
                  v-tooltip="'Edit ' + bank.tooltip"
                  >{{ bank.tooltip }}
                </router-link>
                <span v-else>{{ bank.tooltip }}</span>
              </span>
              <span class="flex justify-end w-16">
                <router-link
                  :to="{
                    name: 'MemberBankDelete',
                    params: { memberId: props.row.uuid, id: bank.uuid },
                  }"
                  class="hover:text-blue-600"
                  v-if="userCan('delete.member-bank-account')"
                  v-tooltip="'Delete ' + bank.tooltip"
                >
                  <font-awesome-icon :icon="['far', 'trash-alt']" />
                </router-link>
                <router-link
                  :to="{
                    name: 'MemberBankTransfer',
                    params: { memberId: props.row.uuid, id: bank.uuid },
                  }"
                  class="hover:text-blue-600 ml-2"
                  v-if="userCan('transfer.member') && bank.uuid"
                  v-tooltip="'Transfer ' + bank.tooltip"
                >
                  <font-awesome-icon :icon="['fas', 'external-link-alt']" />
                </router-link>
              </span>
            </span>
          </template>
          <template v-else>-</template>
        </template>
        <template v-else-if="props.column.field == 'accounts'">
          <template v-if="props.row.accounts.length > 0">
            <span
              class="flex justify-between items-start"
              v-for="account in props.row.accounts"
              :key="account.id"
            >
              <span class="block">
                <router-link
                  v-if="userCan('update.member-account') && account.uuid"
                  :to="{
                    name: 'AccountEdit',
                    params: { memberId: props.row.uuid, id: account.uuid },
                  }"
                  class="link"
                  v-tooltip="'Edit ' + account.tooltip"
                >
                  {{ account.tooltip }}
                </router-link>
                <span v-else>{{ account.tooltip }}</span>
                <span class="block mt-1 mb-2"
                  ><font-awesome-icon :icon="['fas', 'coins']" class="mr-1" />
                  <template v-if="account.balance">{{
                    account.balance
                  }}</template
                  ><template v-else>0.00</template></span
                >
              </span>
              <span class="flex justify-end w-16">
                <router-link
                  :to="{
                    name: 'AccountDelete',
                    params: { memberId: props.row.uuid, id: account.uuid },
                  }"
                  class="hover:text-blue-600"
                  v-if="userCan('delete.member-account') && account.uuid"
                  v-tooltip="'Delete ' + account.tooltip"
                >
                  <font-awesome-icon :icon="['far', 'trash-alt']" />
                </router-link>
                <router-link
                  :to="{
                    name: 'AccountTransfer',
                    params: { memberId: props.row.uuid, id: account.uuid },
                  }"
                  class="hover:text-blue-600 ml-2"
                  v-if="userCan('transfer.member') && account.uuid"
                  v-tooltip="'Transfer ' + account.tooltip"
                >
                  <font-awesome-icon :icon="['fas', 'external-link-alt']" />
                </router-link>
              </span>
            </span>
          </template>
          <template v-else>-</template>
        </template>
        <span v-else-if="props.column.field == 'uuid'">
          <router-link
            v-if="
              userCan('merge.member-bank-account') &&
              props.row.bank_accounts.length > 1
            "
            :to="{
              name: 'MemberBankMerge',
              params: { memberId: props.row.uuid },
            }"
            v-tooltip="'Merge Member Bank Account for ' + props.row.name"
            class="table-action__link"
          >
            <font-awesome-icon
              flip="horizontal"
              :icon="['fas', 'project-diagram']"
            />
          </router-link>
          <router-link
            v-if="userCan('create.member')"
            :to="{
              name: 'AccountCreate',
              params: { memberId: props.row.uuid },
            }"
            v-tooltip="'Create Account for ' + props.row.name"
            class="table-action__link mx-2"
          >
            <font-awesome-icon :icon="['fas', 'user']" />
          </router-link>
          <router-link
            v-if="userCan('create.member')"
            :to="{
              name: 'MemberBankCreate',
              params: { memberId: props.row.uuid },
            }"
            v-tooltip="'Create Bank Account for ' + props.row.name"
            class="table-action__link mx-2"
          >
            <font-awesome-icon :icon="['fas', 'wallet']" />
          </router-link>
          <router-link
            :to="{
              name: 'MemberTransactionHistory',
              params: { memberId: props.row.uuid },
            }"
            v-tooltip="'View Transaction History for ' + props.row.name"
            class="table-action__link mx-2"
            ><font-awesome-icon :icon="['fas', 'search-dollar']"
          /></router-link>
          <router-link
            v-if="userCan('update.member')"
            :to="{ name: 'MemberEdit', params: { id: props.row.uuid } }"
            v-tooltip="'Edit ' + props.row.name"
            class="table-action__link mx-2"
          >
            <font-awesome-icon :icon="['fas', 'edit']" />
          </router-link>
        </span>
      </template>
    </vue-good-table>

    <router-view
      class="mt-4"
      :listAccount="listAccount"
      v-if="listAccountReady"
    ></router-view>
  </div>
</template>

<script>
import { showListMixin } from "@/mixins/showListMixin";
export default {
  mixins: [showListMixin],
  data() {
    return {
      listAccount: [],
      listAccountReady: false,
      tableData: {
        columns: [
          {
            label: "Name",
            field: "name",
          },
          {
            label: "Bank Accounts",
            field: "bank_accounts",
          },
          {
            label: "Account IDs",
            field: "accounts",
          },
          {
            label: "",
            field: "uuid",
            thClass: "vgt-table__action",
            tdClass: "vgt-table__action",
          },
        ],
        rows: [],
      },
    };
  },
  watch: {
    $route(route) {
      if (route.name == "MemberProfile") {
        this.loadTableData();
      }
    },
  },
  created() {
    this.loadTableData();
  },
  methods: {
    dateformat(date) {
      return this.moment(date).format("DD MMM YYYY | HH:mm");
    },
    loadTableData() {
      this.debounce(() => {
        this.listAccountReady = false;
        this.tableData.isLoading = true;
        this.$http
          .get(
            process.env.VUE_APP_API_URL +
              `/member?uuid=${this.$route.params.memberId}&page=1&limit=10`
          )
          .then((res) => {
            this.tableData.rows = [res.data.data];
            this.tableData.totalRows = res.data.total;
            this.listAccount = res.data.data.accounts;
            this.listAccountReady = true;
          });
      }, 500);
    },
  },
};
</script>
